import { type ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';

import { isTerminalStatus, useJob, type JobStatus } from '../api';

function statusVariant(status: JobStatus): 'secondary' | 'destructive' | 'outline' {
  if (status === 'FAILED' || status === 'STOPPED' || status === 'ABANDONED') return 'destructive';
  if (status === 'COMPLETED') return 'secondary';
  return 'outline';
}

/**
 * Inline progress for a launched job. Subscribes via `useJob`, which polls
 * every 2 s while the job is in a non-terminal Spring Batch state and stops
 * once terminal. Surfaces the `exitStatus.exitDescription` when the job FAILED.
 */
export function JobProgressIndicator({
  jobExecutionId,
}: {
  readonly jobExecutionId: number | null;
}): ReactElement | null {
  const { t } = useTranslation();
  const { data: job } = useJob(jobExecutionId);

  if (jobExecutionId === null || job === undefined) return null;

  const running = !isTerminalStatus(job.status);
  const showFailure = job.status === 'FAILED' && job.exitDescription.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px]">
      <span className="font-mono text-muted-foreground">#{job.jobId}</span>
      <span className="text-muted-foreground">{job.jobName}</span>
      <Badge variant={statusVariant(job.status)}>
        {running && <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />}
        {job.status}
      </Badge>
      {showFailure && (
        <span className="text-red-600">
          {t('routes.platform.detail.jobs.status')}: {job.exitDescription}
        </span>
      )}
    </div>
  );
}
